/**
 * useV1Write — helpers d'écriture pour les collections Firestore V1.
 * Cibles : appdata/ep:campagnes, appdata/ep:ressources
 * Structure V1 : { value: [...], updatedAt: Date.now() }
 *
 * Toutes les modifications partent de l'objet existant complet via spread
 * pour ne jamais détruire un champ non édité par V2.
 *
 * Pas de Storage dans cette passe. Pas de suppression définitive.
 */
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase";

const EP_CAMPAGNES  = "ep:campagnes";
const EP_RESSOURCES = "ep:ressources";
const EP_TASKS      = "ep:tasks";
const EP_REQUESTS   = "ep:requests";
const EP_USERS      = "ep:users";
const EP_CLUBS      = "ep:clubs";

function uid() {
  return String(Date.now()) + String(Math.random()).slice(2, 8);
}

/**
 * Synchronise timeline → actions, identique à la logique V1.
 * Préserve clubStatus existant pour chaque action.
 * Clubs = tous les clubs fournis au moment du save.
 */
export function syncTimelineToActions(timeline, existingActions, clubs) {
  return (timeline || [])
    .filter(t => t.label && t.label.trim())
    .map(t => {
      const existing = (existingActions || []).find(a => String(a.id) === String(t.id));
      return {
        id: t.id,
        label: t.label,
        deadline: t.date || "",
        phase: t.phase,
        clubs: (clubs || []).map(c => c.id),
        clubStatus: existing?.clubStatus || {},
        done: t.done || false,
      };
    });
}

/**
 * Valeurs par défaut compatibles EMPTY_FORM() V1.
 * Utilisées à la création pour garantir la compatibilité.
 */
function defaultCampagne() {
  return {
    name: "",
    dateStart: "",
    dateEnd: "",
    persona: "",
    positioning: "",
    concept: "",
    signature: "",
    channels: [],
    hashtags: "",
    pushTitle: "",
    pushMessage: "",
    whatsappMessage: "",
    whatsappMessages: [],
    notifications: [],
    visuels: [],
    pdfName: "Campagne",
    published: false,
    channelNotes: {},
    links: [],
    visualLinks: [],
    visualsTodo: [],
    objectives: [],
    packs: [],
    products: "",
    carteClub: "",
    abonnements: "",
    cours: "",
    coursIndividuel: "",
    kpi: [],
    kpiData: {},
    kpiIndicators: [],
    kpiValues: {},
    targets: [],
    presentation: "",
    timeline: [
      { id: uid(), phase: "Avant",   label: "", date: "", done: false },
      { id: uid(), phase: "Pendant", label: "", date: "", done: false },
      { id: uid(), phase: "Après",   label: "", date: "", done: false },
    ],
    actions: [],
  };
}

/**
 * Crée une nouvelle campagne.
 * @param {Array}  campagnes  tableau courant (depuis useCampagnes hook)
 * @param {Object} formData   champs saisis par l'utilisateur
 * @param {string} appId      id de l'utilisateur connecté (owner)
 * @param {Array}  clubs      liste des clubs (pour sync actions)
 * @returns {Promise<string>} id de la nouvelle campagne
 */
export async function createCampagne(campagnes, formData, appId, clubs) {
  const newId = uid();
  const defaults = defaultCampagne();
  const syncedActions = syncTimelineToActions(
    formData.timeline || defaults.timeline,
    [],
    clubs
  );
  const newCamp = {
    ...defaults,
    ...formData,
    id: newId,
    actions: syncedActions,
    owner: appId,
    createdAt: new Date().toISOString(),
    published: false, // toujours brouillon à la création
  };
  const newArray = [...campagnes, newCamp];
  await setDoc(doc(db, "appdata", EP_CAMPAGNES), {
    value: newArray,
    updatedAt: Date.now(),
  });
  return newId;
}

/**
 * Modifie une campagne existante.
 * Part obligatoirement de l'objet existant pour préserver les champs inconnus.
 * @param {Array}  campagnes       tableau courant
 * @param {string} id              id de la campagne à modifier
 * @param {Object} patch           champs modifiés
 * @param {Array}  clubs           liste des clubs (pour sync actions si timeline modifiée)
 * @returns {Promise<void>}
 */
export async function updateCampagne(campagnes, id, patch, clubs) {
  const existing = campagnes.find(c => String(c.id) === String(id));
  if (!existing) throw new Error(`Campagne introuvable : ${id}`);

  // Si le patch contient une timeline, resynchroniser les actions
  let syncedActions = existing.actions;
  if (patch.timeline !== undefined) {
    syncedActions = syncTimelineToActions(patch.timeline, existing.actions, clubs);
  }

  const updated = {
    ...existing,
    ...patch,
    actions: syncedActions,
  };

  const newArray = campagnes.map(c => String(c.id) === String(id) ? updated : c);
  await setDoc(doc(db, "appdata", EP_CAMPAGNES), {
    value: newArray,
    updatedAt: Date.now(),
  });
}

/**
 * Bascule le statut de suivi d'un club pour une action d'une campagne.
 * Écrit dans action.clubStatus[clubId] et recalcule action.done
 * (= tous les clubs rattachés à l'action ont validé).
 * N'affecte que l'action ciblée ; les autres champs sont préservés.
 * @param {Array}  campagnes  tableau courant
 * @param {string} campagneId id de la campagne
 * @param {string} actionId   id de l'action (= id de l'étape timeline)
 * @param {string} clubId     id du club à basculer
 * @returns {Promise<boolean>} nouveau statut du club (true = fait)
 */
export async function toggleActionClubStatus(campagnes, campagneId, actionId, clubId) {
  const existing = campagnes.find(c => String(c.id) === String(campagneId));
  if (!existing) throw new Error(`Campagne introuvable : ${campagneId}`);

  const cid = String(clubId);
  let newValue = false;

  const newActions = (existing.actions || []).map(a => {
    if (String(a.id) !== String(actionId)) return a;
    const prevStatus = a.clubStatus || {};
    newValue = !prevStatus[cid];
    const clubStatus = { ...prevStatus, [cid]: newValue };
    const actionClubs = (a.clubs || []).map(String);
    const done = actionClubs.length > 0 && actionClubs.every(id => clubStatus[id]);
    return { ...a, clubStatus, done };
  });

  const updated = { ...existing, actions: newActions };
  const newArray = campagnes.map(c => String(c.id) === String(campagneId) ? updated : c);
  await setDoc(doc(db, "appdata", EP_CAMPAGNES), {
    value: newArray,
    updatedAt: Date.now(),
  });
  return newValue;
}

/**
 * Bascule published d'une campagne (publier / repasser en brouillon).
 * @param {Array}  campagnes  tableau courant
 * @param {string} id         id de la campagne
 * @returns {Promise<void>}
 */
export async function togglePublished(campagnes, id) {
  const existing = campagnes.find(c => String(c.id) === String(id));
  if (!existing) throw new Error(`Campagne introuvable : ${id}`);
  const updated = { ...existing, published: !existing.published };
  const newArray = campagnes.map(c => String(c.id) === String(id) ? updated : c);
  await setDoc(doc(db, "appdata", EP_CAMPAGNES), {
    value: newArray,
    updatedAt: Date.now(),
  });
}

// ─── ep:ressources ────────────────────────────────────────────────────────

const MAX_HISTORY = 10;

function addHistory(existing, action, appId) {
  const entry = { at: new Date().toISOString(), by: appId, action };
  const prev = Array.isArray(existing.history) ? existing.history : [];
  return [entry, ...prev].slice(0, MAX_HISTORY);
}

/**
 * Crée une nouvelle ressource numérique (lien ou Canva).
 * Pas d'upload Firebase Storage dans cette passe.
 */
export async function createRessource(ressources, formData, appId) {
  const newId = String(Date.now()) + String(Math.random()).slice(2, 8);
  const now   = new Date().toISOString();
  const newRessource = {
    name: "",
    type: "link",
    url: null,
    externalUrl: null,
    storagePath: null,
    thumbnail: null,
    size: 0,
    mimeType: null,
    category: "autre",
    tags: [],
    documentDate: null,
    season: null,
    description: "",
    campagneIds: [],
    eventIds: [],
    clubs: [],
    published: false,
    archived: false,
    history: [],
    ...formData,
    // Champs système — toujours forcés après spread
    id: newId,
    uploadedBy: appId,
    createdAt: now,
    updatedAt: now,
    archived: false,
  };
  newRessource.history = [{ at: now, by: appId, action: "created" }];
  const newArray = [...ressources, newRessource];
  await setDoc(doc(db, "appdata", EP_RESSOURCES), {
    value: newArray,
    updatedAt: Date.now(),
  });
  return newId;
}

/**
 * Modifie une ressource existante.
 * Spread de l'objet existant pour préserver les champs inconnus.
 */
export async function updateRessource(ressources, id, patch, appId) {
  const existing = ressources.find(r => String(r.id) === String(id));
  if (!existing) throw new Error(`Ressource introuvable : ${id}`);
  const updated = {
    ...existing,
    ...patch,
    id: existing.id,
    uploadedBy: existing.uploadedBy,
    createdAt: existing.createdAt,
    updatedAt: new Date().toISOString(),
    history: addHistory(existing, "updated", appId),
  };
  const newArray = ressources.map(r => String(r.id) === String(id) ? updated : r);
  await setDoc(doc(db, "appdata", EP_RESSOURCES), {
    value: newArray,
    updatedAt: Date.now(),
  });
}

/**
 * Bascule published d'une ressource.
 */
export async function toggleRessourcePublished(ressources, id, appId) {
  const existing = ressources.find(r => String(r.id) === String(id));
  if (!existing) throw new Error(`Ressource introuvable : ${id}`);
  const action = existing.published ? "unpublished" : "published";
  const updated = {
    ...existing,
    published: !existing.published,
    updatedAt: new Date().toISOString(),
    history: addHistory(existing, action, appId),
  };
  const newArray = ressources.map(r => String(r.id) === String(id) ? updated : r);
  await setDoc(doc(db, "appdata", EP_RESSOURCES), {
    value: newArray,
    updatedAt: Date.now(),
  });
}

/**
 * Archive ou désarchive une ressource.
 */
export async function archiveRessource(ressources, id, archive, appId) {
  const existing = ressources.find(r => String(r.id) === String(id));
  if (!existing) throw new Error(`Ressource introuvable : ${id}`);
  const updated = {
    ...existing,
    archived: archive,
    updatedAt: new Date().toISOString(),
    history: addHistory(existing, archive ? "archived" : "unarchived", appId),
  };
  const newArray = ressources.map(r => String(r.id) === String(id) ? updated : r);
  await setDoc(doc(db, "appdata", EP_RESSOURCES), {
    value: newArray,
    updatedAt: Date.now(),
  });
}

// ─── ep:tasks / ep:requests — attribution d'une demande ─────────────────────

/**
 * Lit appdata/{key}, applique mutate(value[]) → nouveau tableau, puis réécrit.
 * Relecture fraîche (getDoc) juste avant l'écriture pour limiter l'écrasement
 * de modifications concurrentes (le reste du module part du tableau du hook).
 */
async function patchAppdataArray(key, mutate) {
  const ref = doc(db, "appdata", key);
  const snap = await getDoc(ref);
  const body = snap.exists() ? snap.data() : {};
  const current = Array.isArray(body && body.value) ? body.value.slice() : [];
  const next = mutate(current);
  await setDoc(ref, { value: next, updatedAt: Date.now() });
  return next;
}

/** Ids de clubs d'une entité (gère `clubs[]` + `club` singulier). */
function clubIdsOfEntity(entity) {
  const arr = Array.isArray(entity && entity.clubs) ? entity.clubs.map(String) : [];
  if (entity && entity.club != null && entity.club !== "" && !arr.includes(String(entity.club))) {
    arr.push(String(entity.club));
  }
  return arr;
}

/** Valeurs par défaut d'une tâche (ep:tasks). Statuts : À faire | En cours | En validation | Terminée | Bloquée. */
export function defaultTask() {
  return {
    title: "",
    description: "",
    status: "À faire",
    priority: "Normale",
    assignedTo: [],
    assigneeId: "",
    clubs: [],
    campagneId: "",
    requestId: "",
    deadline: "",
    subtasks: [],
    history: [],
    source: "",
  };
}

/**
 * Attribue (ou ré-attribue) une demande à une personne.
 *
 * Met à jour la TÂCHE liée (ep:tasks) — porteuse du suivi réel — puis la DEMANDE
 * (ep:requests) pour que la colonne « Assigné à » reflète immédiatement le choix.
 * Spread systématique de l'objet existant : aucun champ non édité n'est détruit.
 *
 * @param {object}  p
 * @param {object}  p.request        la demande (ep:requests) à attribuer
 * @param {string}  p.assigneeId     id de la personne choisie (obligatoire)
 * @param {string}  [p.assigneeName] nom lisible (pour l'historique de la tâche)
 * @param {string}  [p.deadline]     échéance (yyyy-mm-dd)
 * @param {string}  [p.priority]     "Basse" | "Normale" | "Haute" | "Urgente"
 * @param {string}  [p.note]         mot pour la personne (optionnel)
 * @param {string}  [p.campagneId]   campagne rattachée (optionnel)
 * @param {string}  [p.actorId]      id de l'utilisateur qui attribue (Mélissa)
 */
export async function attribuerDemande({
  request, assigneeId, assigneeName, deadline, priority, note, campagneId, actorId,
}) {
  if (!request || !assigneeId) {
    throw new Error("Attribution impossible : demande ou destinataire manquant.");
  }

  const nowIso = new Date().toISOString();
  const who = assigneeName || "la personne choisie";

  // Historise l'attribution sur la tâche (création ou ré-attribution).
  const buildHistory = (prev) => {
    const history = Array.isArray(prev) ? prev.slice() : [];
    const parts = [`Demande attribuée à ${who}`];
    if (priority) parts.push(`priorité ${priority}`);
    if (deadline) parts.push(`échéance ${deadline}`);
    history.push({ id: uid(), at: nowIso, userId: actorId || "", text: parts.join(" · ") });
    if (note && note.trim()) {
      history.push({ id: uid(), at: nowIso, userId: actorId || "", text: `Note : ${note.trim()}` });
    }
    return history;
  };

  /* 1. Tâche liée — porte le suivi opérationnel réel.
   * Beaucoup de demandes (formulaire public V2, email) n'ont PAS encore de tâche :
   * on la CRÉE alors, pour que « Projets & Tâches » ait une entrée homogène. */
  let taskId = request.taskId ? String(request.taskId) : "";

  if (taskId) {
    await patchAppdataArray(EP_TASKS, (tasks) =>
      tasks.map((t) => {
        if (!t || String(t.id) !== taskId) return t;
        return {
          ...t,
          assignedTo: [assigneeId],
          assigneeId,
          deadline: deadline || t.deadline || "",
          priority: priority || t.priority || "Normale",
          // Une tâche attribuée mais pas encore démarrée reste "À faire".
          status: t.status && t.status !== "Terminée" ? t.status : "À faire",
          ...(campagneId ? { campagneId } : {}),
          history: buildHistory(t.history),
        };
      })
    );
  } else {
    taskId = uid();
    const newTask = {
      ...defaultTask(),
      id: taskId,
      title: request.subject || "Demande club",
      description: request.description || "",
      clubs: clubIdsOfEntity(request),
      requestId: request.id != null ? String(request.id) : "",
      source: "demande",
      assignedTo: [assigneeId],
      assigneeId,
      deadline: deadline || "",
      priority: priority || "Normale",
      status: "À faire",
      ...(campagneId ? { campagneId } : {}),
      createdBy: actorId || "",
      createdAt: nowIso,
      updatedAt: nowIso,
      history: buildHistory([]),
    };
    await patchAppdataArray(EP_TASKS, (tasks) => [...tasks, newTask]);
  }

  /* 2. Demande — reflète l'assignation dans la liste du Centre et mémorise le
   * lien vers la tâche (créée ci-dessus le cas échéant). */
  await patchAppdataArray(EP_REQUESTS, (reqs) =>
    reqs.map((r) => {
      if (!r || String(r.id) !== String(request.id)) return r;
      return {
        ...r,
        taskId,
        assigneeId,
        assignedTo: [assigneeId],
        deadline: deadline || r.deadline || "",
        priority: priority || r.priority || "Normale",
        ...(campagneId ? { campagneId } : {}),
        ...(note && note.trim() ? { attributionNote: note.trim() } : {}),
        attributedAt: nowIso,
      };
    })
  );

  return taskId;
}

// ─── ep:tasks — écritures depuis « Projets & Tâches » ───────────────────────

/**
 * Crée une tâche libre (hors demande).
 * @param {Object} formData  champs saisis (title, description, status, priority,
 *                           assignedTo[], clubs[], campagneId, deadline, subtasks[]…)
 * @param {string} appId     id de l'utilisateur créateur
 * @returns {Promise<string>} id de la nouvelle tâche
 */
export async function createTask(formData, appId) {
  const newId = uid();
  const now = new Date().toISOString();
  const title = (formData && formData.title ? String(formData.title) : "").trim();
  const newTask = {
    ...defaultTask(),
    ...formData,
    id: newId,
    title: title || "Nouvelle tâche",
    createdBy: appId || "",
    createdAt: now,
    updatedAt: now,
    history: [{ id: uid(), at: now, userId: appId || "", text: "Tâche créée" }],
  };
  await patchAppdataArray(EP_TASKS, (tasks) => [...tasks, newTask]);
  return newId;
}

/**
 * Modifie une tâche. Spread de l'existant : aucun champ non édité n'est détruit.
 */
export async function updateTask(id, patch, appId) {
  const now = new Date().toISOString();
  await patchAppdataArray(EP_TASKS, (tasks) =>
    tasks.map((t) => {
      if (!t || String(t.id) !== String(id)) return t;
      return { ...t, ...patch, id: t.id, updatedAt: now };
    })
  );
}

/**
 * Change le statut d'une tâche (déplacement de colonne) + trace l'historique.
 */
export async function setTaskStatus(id, status, appId) {
  const now = new Date().toISOString();
  await patchAppdataArray(EP_TASKS, (tasks) =>
    tasks.map((t) => {
      if (!t || String(t.id) !== String(id)) return t;
      const history = Array.isArray(t.history) ? t.history.slice() : [];
      history.push({ id: uid(), at: now, userId: appId || "", text: `Statut : ${status}` });
      return { ...t, status, updatedAt: now, history };
    })
  );
}

/**
 * Coche / décoche une sous-tâche.
 */
export async function toggleSubtask(taskId, subId, appId) {
  const now = new Date().toISOString();
  await patchAppdataArray(EP_TASKS, (tasks) =>
    tasks.map((t) => {
      if (!t || String(t.id) !== String(taskId)) return t;
      const subs = Array.isArray(t.subtasks) ? t.subtasks : [];
      return {
        ...t,
        subtasks: subs.map((s) => (String(s.id) === String(subId) ? { ...s, done: !s.done } : s)),
        updatedAt: now,
      };
    })
  );
}

// ─── ep:users — gestion de l'équipe (page Équipe, admin uniquement) ──────────
// L'écriture de ep:users est réservée aux admins CÔTÉ SERVEUR (server/lib/access.js :
// canWriteKey). La page Équipe n'est de toute façon montrée qu'aux admins. Le rôle
// est stocké dans le champ canonique `roleComm` (admin|comm|directeur|ja) ; on
// maintient en miroir `admin` (booléen) et `role` (texte) pour la rétro-compat des
// lectures existantes. Aucune suppression définitive : on désactive (active:false).

/** Nom d'affichage dérivé de prénom/nom (fallback : name existant). */
function fullName(rec) {
  return [rec.firstName, rec.lastName].filter(Boolean).join(" ").trim() || rec.name || "";
}

/** Champs miroirs de compatibilité à partir du rôle canonique. */
function roleMirror(roleComm) {
  const role = String(roleComm || "comm").toLowerCase();
  return {
    roleComm: role,
    admin: role === "admin",
    // `role` texte conservé pour les lectures historiques (ex. label sidebar).
    role: { admin: "Responsable comm", comm: "Référent comm", directeur: "Directeur", ja: "Juge-arbitre" }[role] || "Référent comm",
  };
}

/**
 * Crée un membre de l'équipe.
 * @param {Object} formData  { firstName, lastName, email, roleComm, clubs[], active }
 * @returns {Promise<number|string>} id du nouveau membre
 */
export async function createUser(formData) {
  const now = new Date().toISOString();
  // id numérique (style dominant de ep:users) ; unique par création manuelle.
  const id = Date.now();
  const rec = {
    firstName: "",
    lastName: "",
    email: "",
    clubs: [],
    active: true,
    ...formData,
    ...roleMirror(formData && formData.roleComm),
    id,
    createdAt: now,
    updatedAt: now,
  };
  rec.email = String(rec.email || "").toLowerCase().trim();
  rec.clubs = Array.isArray(rec.clubs) ? rec.clubs.map(String) : [];
  rec.name = fullName(rec);
  await patchAppdataArray(EP_USERS, (users) => [...users, rec]);
  return id;
}

/**
 * Modifie un membre. Spread de l'existant : aucun champ non édité n'est détruit
 * (notamment `password`, jamais touché ici).
 */
export async function updateUser(id, patch) {
  const now = new Date().toISOString();
  await patchAppdataArray(EP_USERS, (users) =>
    users.map((u) => {
      if (!u || String(u.id) !== String(id)) return u;
      const merged = { ...u, ...patch, id: u.id, updatedAt: now };
      if (patch.roleComm !== undefined) Object.assign(merged, roleMirror(patch.roleComm));
      if (patch.clubs !== undefined) merged.clubs = Array.isArray(patch.clubs) ? patch.clubs.map(String) : [];
      if (patch.email !== undefined) merged.email = String(patch.email || "").toLowerCase().trim();
      if (patch.firstName !== undefined || patch.lastName !== undefined) merged.name = fullName(merged);
      return merged;
    })
  );
}

/** Active / désactive un membre (pas de suppression définitive). */
export async function setUserActive(id, active) {
  return updateUser(id, { active: !!active });
}

// ─── ep:clubs — paramètres clubs & périmètre (page Paramètres, admin) ────────
// Les clubs sont des entités STRUCTURELLES : référencées par id dans user.clubs,
// les demandes, les tâches, le reporting… On ne supprime donc jamais un club ;
// on l'archive (archived:true) pour le retirer de la gestion sans casser les
// références existantes. Spread systématique de l'existant pour préserver tout
// champ inconnu écrit par la V1 (blogId Metricool, etc.).
//
// Champs édités ici :
//   name            — nom du club (obligatoire)
//   ville           — ville (affichage)
//   ga4PropertyId   — ID de propriété GA4 (branche le « Web » au reporting)
//   metricoolBlogId — ID de blog/brand Metricool (réseaux IG/FB/GMB)
//   archived        — retiré de la gestion (références conservées)

/** Nettoie un patch club : trim des chaînes connues, clubs = tableau d'ids. */
function cleanClubPatch(patch) {
  const out = { ...patch };
  ["name", "ville", "ga4PropertyId", "metricoolBlogId"].forEach((k) => {
    if (out[k] !== undefined) out[k] = String(out[k] || "").trim();
  });
  return out;
}

/**
 * Crée un club.
 * @param {Object} formData { name, ville?, ga4PropertyId?, metricoolBlogId? }
 * @returns {Promise<string>} id du nouveau club
 */
export async function createClub(formData) {
  const now = new Date().toISOString();
  const id = uid();
  const rec = {
    name: "",
    ville: "",
    ga4PropertyId: "",
    metricoolBlogId: "",
    archived: false,
    ...cleanClubPatch(formData || {}),
    id,
    createdAt: now,
    updatedAt: now,
  };
  await patchAppdataArray(EP_CLUBS, (clubs) => [...clubs, rec]);
  return id;
}

/**
 * Modifie un club. Spread de l'existant : aucun champ non édité n'est détruit.
 */
export async function updateClub(id, patch) {
  const now = new Date().toISOString();
  const clean = cleanClubPatch(patch || {});
  await patchAppdataArray(EP_CLUBS, (clubs) =>
    clubs.map((c) => {
      if (!c || String(c.id) !== String(id)) return c;
      return { ...c, ...clean, id: c.id, updatedAt: now };
    })
  );
}

/** Archive / désarchive un club (jamais de suppression définitive). */
export async function setClubArchived(id, archived) {
  return updateClub(id, { archived: !!archived });
}
